import { field, type FormTemplate, type Run } from './types'

/**
 * Flattens a filled-in template to plain text, and fingerprints it.
 *
 * A port of `DocumentText.kt`, and it has to stay a faithful one: the hash on a
 * PDF signed in the browser must match the hash the Android client would
 * produce for the same document, or the fingerprint means nothing.
 */

/** Shown where a field was left empty. */
export const BLANK = '(not given)'

export function plainText(template: FormTemplate, values: Record<string, string>): string {
  let out = ''
  for (const block of template.body) {
    switch (block.kind) {
      case 'heading':
      case 'paragraph':
      case 'note':
        out += `${resolveRuns(block.runs, values)}\n\n`
        break
      case 'bullet':
        out += `${block.marker} ${resolveRuns(block.runs, values)}\n`
        break
      case 'rule':
        out += '----\n\n'
        break
      case 'fieldLine': {
        const label = field(template, block.key)?.label ?? block.key
        out += `${label}: ${valueOf(values, block.key)}\n\n`
        break
      }
      case 'signature': {
        const label = template.signatures.find((s) => s.key === block.key)?.label ?? block.key
        out += `${label}: [signed]\n\n`
        break
      }
    }
  }
  return `${out.replace(/\s+$/, '')}\n`
}

export function resolveRuns(runs: Run[], values: Record<string, string>): string {
  return runs.map((r) => (r.placeholder ? valueOf(values, r.placeholder) : r.text)).join('')
}

export function valueOf(values: Record<string, string>, key: string): string {
  const raw = (values[key] ?? '').trim()
  return raw || BLANK
}

/**
 * Lowercase hex SHA-256 of the document text followed by each signature image,
 * in template order. Same input, same hash, on either client.
 */
export async function fingerprint(
  documentText: string,
  signatureBytes: Uint8Array[],
): Promise<string> {
  const encoded = new TextEncoder().encode(documentText)
  let total = encoded.length
  for (const bytes of signatureBytes) total += bytes.length

  const buffer = new Uint8Array(total)
  buffer.set(encoded, 0)
  let offset = encoded.length
  for (const bytes of signatureBytes) {
    buffer.set(bytes, offset)
    offset += bytes.length
  }

  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
